/**
 * Help guide content.
 *
 * Deliberately a plain data module rather than dictionary entries: these are
 * support docs, not marketing copy, and — like /refunds and /terms — they ship
 * English-only for now. Adding a locale later means moving `title`/`body` into
 * messages/*.json; the route reads whatever this file exports.
 *
 * Numbers in the rewards guide are pulled from lib/rewards.ts. If POINTS_CONFIG
 * or REWARD_TIERS change, this file has to change with them.
 *
 * Voice: task-first, like Vercel docs and Webflow University — short steps,
 * clear outcomes, no filler. Say where to click and what changes when you're done.
 */

import type { PlateKind } from "@/components/help/help-plates"
import { formatGuideCategory } from "@/lib/journal-categories"

export type GuideAudience = "everyone" | "owner" | "barber"

export type GuideStep = {
  title: string
  body: string
}

export type Guide = {
  slug: string
  title: string
  summary: string
  audience: GuideAudience
  minutes: number
  /** Which drawn plate fronts this guide — see components/help/help-plates. */
  plate: PlateKind
  /** Caption printed under the plate, drafting-sheet style. */
  plateCaption: string
  /**
   * The guide's accent, as a space-separated RGB triple so it can be poured
   * into `rgb(<accent> / <alpha>)` at any opacity. Each guide owns a hue —
   * it tints the plate's gradient, the category dot, the step numbers and the
   * live marker in the outline, which is what makes five pages built from one
   * template read as five different subjects.
   */
  accent: string
  /**
   * Partner whose product the guide is about. Renders as a mark on
   * the plate and a credit line under the lede. Only set where the
   * integration is live — this is a trust signal, not decoration.
   */
  brand?: { src: string; alt: string; note: string }
  steps: GuideStep[]
  /** Short closers — notes after the steps. */
  tips?: string[]
  /** Where the reader goes once they're done. */
  cta?: { label: string; href: string }
}

export const AUDIENCE_LABEL: Record<GuideAudience, string> = {
  everyone: formatGuideCategory("everyone"),
  owner: formatGuideCategory("owner"),
  barber: formatGuideCategory("barber"),
}

export const HELP_GUIDES: Guide[] = [
  {
    slug: "list-your-shop",
    title: "List your shop",
    summary:
      "Create a shop listing, add open chairs, and publish so barbers can browse and apply from the BOOTH directory.",
    audience: "owner",
    minutes: 10,
    plate: "plan",
    plateCaption: "Floor plan",
    accent: "59 130 246",
    steps: [
      {
        title: "Create an owner account",
        body: "Sign up as an owner. Your dashboard stays empty until you add your first shop.",
      },
      {
        title: "Add shop details",
        body: "Go to Dashboard → Create shop. Enter the shop name, address, and basic details. The address determines map placement — use the location clients would search for.",
      },
      {
        title: "Upload shop photos",
        body: "Add photos of the storefront, floor, stations, and the chair you are renting. Listings with multiple shop photos receive more inquiries than listings with only a logo.",
      },
      {
        title: "Create rental plans",
        body: "Add a plan for each chair: weekly, biweekly, or monthly pricing, plus what's included (station, storage, products, front desk, and so on). Barbers compare plans on price and inclusions.",
      },
      {
        title: "Publish and connect Stripe",
        body: "Publishing adds the shop to public listings. Connect Stripe to collect rent through BOOTH. Complete both before inviting barbers.",
      },
    ],
    tips: [
      "Already use Booksy, Fresha, Squire, or The Cut? Add your booking link on the listing so clients can book through your existing tool.",
      "List amenities such as parking, laundry, product access, and extended hours — they often decide between similar listings.",
      "One account supports multiple locations. Each shop has its own listing, plans, and payment history.",
    ],
    cta: { label: "Create your shop", href: "/dashboard/owner/create-shop" },
  },
  {
    slug: "onboard-your-barbers",
    title: "Onboard barbers to your shop",
    summary:
      "Move a barber from inquiry to an active chair with a signed agreement and scheduled rent payments.",
    audience: "owner",
    minutes: 8,
    plate: "sign",
    plateCaption: "The agreement",
    accent: "149 118 255",
    steps: [
      {
        title: "Invite or accept a barber",
        body: "Invite someone you already work with from your shop page, or accept an inquiry from a barber who found your listing. Both paths attach the barber to your shop roster.",
      },
      {
        title: "Assign a rental plan",
        body: "Select the plan and chair for this barber. The plan sets the amount and billing cycle — you do not enter rent manually each period.",
      },
      {
        title: "Send the rental agreement",
        body: "Generate the booth rental contract from your shop page and send it for signature. The barber signs on their phone; both parties receive a signed PDF with your logo.",
      },
      {
        title: "Enable autopay",
        body: "With a card on file, the plan renews on schedule — weekly, biweekly, or monthly. Autopay removes manual rent collection.",
      },
      {
        title: "Track payments in the dashboard",
        body: "Scheduled payments shows upcoming charges. Transactions shows completed payments. Status updates per barber and per chair when money moves.",
      },
    ],
    tips: [
      "Create the plan before sending the contract so the agreement matches the terms in your dashboard.",
      "Barbers who pay through BOOTH earn rewards points on rent at no cost to you — a useful incentive to keep payments on-platform.",
    ],
    cta: { label: "Open shop dashboard", href: "/dashboard/owner" },
  },
  {
    slug: "connect-stripe",
    title: "Connect Stripe and receive payouts",
    summary:
      "Link a Stripe account once so booth rent, bookings, and memberships deposit to your bank automatically.",
    audience: "everyone",
    minutes: 5,
    plate: "rail",
    plateCaption: "Payout connection",
    accent: "52 199 89",
    brand: {
      src: "/images/stripe-logo.png",
      alt: "Stripe",
      note: "Payments and payouts run on Stripe. BOOTH never stores your bank details.",
    },
    steps: [
      {
        title: "Open Account settings",
        body: "Sign in and go to Dashboard → Account. The Payments section at the top shows whether payouts are enabled or disabled.",
      },
      {
        title: "Select Connect with Stripe",
        body: "Stripe opens in a new window. Complete the flow in one session — closing and restarting can create a duplicate account instead of continuing the original setup.",
      },
      {
        title: "Complete Stripe verification",
        body: "Stripe collects your legal name, date of birth, address, and — in the US — the last four digits of your SSN. Business accounts also require a business type and EIN. BOOTH does not store this information; it is submitted directly to Stripe.",
      },
      {
        title: "Add a payout bank account",
        body: "Enter routing and account numbers, or connect through instant bank login. Use the account where you want payouts deposited.",
      },
      {
        title: "Confirm payouts are enabled",
        body: "Return to your Account page in BOOTH. When the status shows Payouts enabled, rent and bookings paid through BOOTH will settle to your connected account.",
      },
    ],
    tips: [
      "First payouts may take longer while Stripe verifies your account. After verification, standard timing is a few business days.",
      "BOOTH charges a 5% platform fee on each payment. Card processing fees appear on your Stripe account, not on BOOTH's.",
      "If status stays Payouts disabled, Stripe is usually waiting on a document. Open Stripe from your Account page to see what's missing.",
    ],
    cta: { label: "Go to Account", href: "/dashboard/account" },
  },
  {
    slug: "get-hired",
    title: "Find a chair or get hired",
    summary:
      "Build your profile, browse open chairs, and complete onboarding once you and an owner agree on terms.",
    audience: "barber",
    minutes: 7,
    plate: "grid",
    plateCaption: "Open chairs",
    accent: "226 78 72",
    steps: [
      {
        title: "Complete your profile",
        body: "Add portfolio photos, specialties, city, and license details before messaging owners. Owners review profiles before reading messages — incomplete profiles are often skipped.",
      },
      {
        title: "Browse open chairs",
        body: "Go to Listings to see shops renting booths. Each card shows price and terms. Filter by city and price, and save listings to review later.",
      },
      {
        title: "Apply to job posts",
        body: "The job board lists open chairs and roles from shop owners. Tap Inquire to send your profile and open a message thread.",
      },
      {
        title: "Message the owner",
        body: "Use Messages to ask about rent inclusions, hours, walk-ins, product policy, and how shop-sourced clients are handled.",
      },
      {
        title: "Sign and set up payments",
        body: "After you agree on a plan, sign the rental agreement on your phone and connect payments so rent runs on schedule and your bookings pay out to you.",
      },
    ],
    tips: [
      "Six to nine strong portfolio photos outperform large galleries. Show range — fades, line-ups, beards — without repeating the same cut.",
      "Reply quickly. Open chairs are often filled by the first responsive applicant.",
      "Rent paid through BOOTH earns rewards points from day one. Set up payments when you join, not after your first month.",
    ],
    cta: { label: "Browse open chairs", href: "/listings" },
  },
  {
    slug: "earn-points",
    title: "Earn and redeem rewards points",
    summary:
      "Points accrue on booth rent paid through BOOTH and can be redeemed for equipment and supplies in the rewards shop.",
    audience: "barber",
    minutes: 4,
    plate: "dial",
    plateCaption: "Points multiplier",
    accent: "229 255 0",
    steps: [
      {
        title: "Pay rent through BOOTH",
        body: "Each dollar of booth rent paid on the platform earns 1 point. No opt-in is required — points are calculated on the payment.",
      },
      {
        title: "Turn on autopay for 2× points",
        body: "Rent on autopay earns at double the base rate. Enable autopay in your payment settings.",
      },
      {
        title: "Claim starting bonuses",
        body: "100 points on your first payment, 250 for joining your first shop, and 500 when someone you referred joins and starts paying.",
      },
      {
        title: "Maintain on-time payments",
        body: "Milestone bonuses apply automatically: 500 points at 3 months, 1,000 at 6, and 2,500 at 12 consecutive on-time payments.",
      },
      {
        title: "Advance through tiers",
        body: "Lifetime points set your multiplier: Bronze 1×, Silver at 1,000 (1.1×), Gold at 5,000 (1.25×), Platinum at 15,000 (1.5×). The tier applies to all points earned after you reach it.",
      },
      {
        title: "Redeem in the rewards shop",
        body: "100 points equals $1 toward equipment and supplies — clippers, chairs, capes, and more. Items ship to you; points are not store credit toward rent.",
      },
    ],
    tips: [
      "Points do not expire while your account is active.",
      "View your balance, tier, and history on the Rewards page in your dashboard.",
    ],
    cta: { label: "Open rewards shop", href: "/rewards" },
  },
]

export function getGuide(slug: string): Guide | undefined {
  return HELP_GUIDES.find((g) => g.slug === slug)
}
